import * as THREE from "three";

import { varGap } from "@/scripts/utils/variables";

export function createSwapShaderMaterial(meshAspect: number): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
        uniforms: {
            uTextureA: { value: null },
            uTextureB: { value: null },
            uAspectA: { value: meshAspect },
            uAspectB: { value: meshAspect },
            uMeshAspect: { value: meshAspect },
            uProgress: { value: 0 },
            uDirection: { value: 1 },
            uOpacity: { value: 0 },
            uOffset: { value: new THREE.Vector2(0, 0) },
            uTime: { value: 0 },
            uSquircleProgress: { value: 0.6 },
            uMaxRadiusPow: { value: 64 },
            uSharpCorners: { value: 0 },
            uChromaticAberration: { value: 0.5 },
            uLensDistortion: { value: 0.6 },
            uFocusFactor: { value: 0.86 },
            uRoll: { value: 0 },
            uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
            uGap: { value: Math.max(varGap, 1) },
            uRgbShiftStrength: { value: 0.35 },
            uRgbShiftBoost: { value: 2.5 },
            uOffsetParallax: { value: 1 },
        },

        vertexShader: /* glsl */ `
          uniform vec2 uOffset;

          varying vec2 vUv;

          vec3 deformationCurve(vec3 pos, vec2 uv, vec2 offset) {
            float M_PI = 3.1415926535897932384626433832795;
            pos.y += sin(uv.x * M_PI) * offset.y;
            return pos;
          }

          void main() {
            vUv = uv;
            vec3 newPosition = deformationCurve(position, uv, uOffset);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
          }
        `,

        fragmentShader: /* glsl */ `
          uniform sampler2D uTextureA;
          uniform sampler2D uTextureB;
          uniform float uAspectA;
          uniform float uAspectB;
          uniform float uMeshAspect;
          uniform float uProgress;
          uniform float uDirection;
          uniform float uOpacity;
          uniform vec2 uOffset;
          uniform float uRgbShiftStrength;
          uniform float uRgbShiftBoost;
          uniform float uOffsetParallax;
          uniform float uSquircleProgress;
          uniform float uMaxRadiusPow;
          uniform float uSharpCorners;
          uniform float uChromaticAberration;
          uniform float uLensDistortion;
          uniform float uFocusFactor;
          uniform float uRoll;
          uniform float uPixelRatio;
          uniform float uGap;

          varying vec2 vUv;

          vec2 coverUv(vec2 uv, float imageAspect) {
            vec2 s = vec2(1.0);
            if (imageAspect > uMeshAspect) s.x = uMeshAspect / imageAspect;
            else s.y = imageAspect / uMeshAspect;
            return (uv - 0.5) * s + 0.5;
          }

          vec2 safeUv(vec2 uv) {
            return clamp(uv, vec2(0.002), vec2(0.998));
          }

          float superellipse(vec2 p, float n) {
            p = abs(p);
            return pow(pow(p.x, n) + pow(p.y, n), 1.0 / n) - 1.0;
          }

          float squircleBoxSDF(vec2 p, float radius) {
            vec2 extent = uMeshAspect > 1.0 ? vec2(uMeshAspect, 1.0) : vec2(1.0, 1.0 / uMeshAspect);
            vec2 q = abs(p) * extent - (extent - radius);
            vec2 c = pow(max(q, vec2(0.0)) / max(radius, 0.0001), vec2(4.0));
            return radius * pow(c.x + c.y, 0.25) + min(max(q.x, q.y), 0.0) - radius;
          }

          float currentSDF(vec2 p) {
            float t = pow(smoothstep(0.0, 1.0, uSquircleProgress - abs(uRoll) * 0.1), 3.0);
            float squircle = superellipse(p, mix(4.0, uMaxRadiusPow, t));
            float squircleBox = squircleBoxSDF(p, 0.6 * (1.0 - smoothstep(0.75, 1.0, uSquircleProgress - abs(uRoll) * 0.1)));
            return mix(squircle, squircleBox, uSharpCorners);
          }

          float edgeZone(vec2 p) {
            vec2 fw = max(fwidth(p), vec2(0.00001));
            float unit = (fw.x + fw.y) * 0.5;

            return uGap * uPixelRatio * unit;
          }

          // Radial lens split + directional shift from the drag lag
          vec3 sampleLayer(sampler2D tex, float aspect, vec2 uv, float pull, float split, vec2 shift) {
            vec2 c = uv - 0.5;
            vec2 uvR = safeUv(coverUv(0.5 + c * (1.0 - pull - split), aspect) + shift);
            vec2 uvG = safeUv(coverUv(0.5 + c * (1.0 - pull), aspect));
            vec2 uvB = safeUv(coverUv(0.5 + c * (1.0 - pull + split * 0.6), aspect) - shift);
            return vec3(
              texture2D(tex, uvR).r,
              texture2D(tex, uvG).g,
              texture2D(tex, uvB).b
            );
          }

          void main() {
            vec2 p = vUv * 2.0 - 1.0;

            // ── Squircle mask ──
            float sdf = currentSDF(p);
            float fw = fwidth(sdf);
            float mask = 1.0 - smoothstep(0.0, fw * 3.0, sdf);
            if (mask < 0.001) discard;

            float t = smoothstep(0.0, 1.0, uProgress);
            float burst = sin(t * 3.14159265);

            // ── Focus-zone lens distortion ──
            float zone = edgeZone(p);
            float band = smoothstep(-zone, 0.0, sdf);
            float corner = clamp(abs(p.x) * abs(p.y), 0.0, 1.0);
            float pull = (uLensDistortion + burst * 0.4) * zone * 3.0 * band * mix(0.45, 1.0, corner);

            // Faster movement widens the split — ramps, then saturates
            float speed = length(uOffset);
            float speedFactor = 1.0 - exp(-speed * uRgbShiftBoost);
            float split = pull * (uChromaticAberration * 0.35 + burst * 0.35 + speedFactor * 0.6);

            vec2 shift = uOffset * uRgbShiftStrength * (1.0 + speedFactor);
            shift.x += uRoll * 0.012;

            // Texture trails behind the movement
            vec2 parallax = uOffset * uOffsetParallax;

            // Outgoing pushes away, incoming arrives from the hovered direction
            float travel = 0.14;
            vec2 uvA = vUv + parallax + vec2(0.0, uDirection * travel * t);
            vec2 uvB = vUv + parallax - vec2(0.0, uDirection * travel * (1.0 - t));
            uvA = (uvA - 0.5) / mix(1.0, 0.92, t) + 0.5;
            uvB = (uvB - 0.5) / mix(1.08, 1.0, t) + 0.5;

            vec3 colA = sampleLayer(uTextureA, uAspectA, uvA, pull, split, shift);

            vec3 color;
            if (uProgress <= 0.001) {
              color = colA;
            } else {
              vec3 colB = sampleLayer(uTextureB, uAspectB, uvB, pull, split, shift);

              // Soft directional wipe front
              float axis = uDirection > 0.0 ? vUv.y : 1.0 - vUv.y;
              float soft = 0.45;
              float edge = t * (1.0 + soft) - soft;
              float blend = 1.0 - smoothstep(edge, edge + soft, axis);

              color = mix(colA, colB, blend);
              color += (1.0 - smoothstep(0.0, 0.08, abs(axis - edge - soft * 0.5))) * burst * 0.12;
            }

            float vignette = smoothstep(-0.7, 0.0, sdf) * mix(0.5, 1.0, corner);
            color *= 1.0 - vignette * 0.2;
            color += exp(-abs(sdf) * 30.0) * (color * 0.2 + vec3(0.012, 0.014, 0.012));

            gl_FragColor = vec4(color, mask * uOpacity);
          }
        `,

        transparent: true,
        depthWrite: false,
    });
}
